import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { usePermissions } from "../../contexts/permissions_context";
import RedirectIfAuthenticated from "./redirect_to_dashboard";
import ProtectedRoute from "./protected_route";
import AppShell from "../../components/app_shell";

// Public / no-shell pages — loaded lazily but shown full-screen (no sidebar)
const LandingPage                     = lazy(() => import("../landing/landing_page"));
const Register                        = lazy(() => import("../register/register"));
const EmployeeOnBoarding              = lazy(() => import("../register/employee_on_boarding"));
const ClientOnBoarding                = lazy(() => import("../register/client_onboarding"));
const ResetPassword                   = lazy(() => import("../reset_password/reset_password"));
const SignIn                          = lazy(() => import("../sign_in/sign_in"));
const ClientSignIn                    = lazy(() => import("../sign_in/client_sign_in"));
const TermsAndConditions              = lazy(() => import("../policies/terms_and_conditions"));
const PrivacyPolicy                   = lazy(() => import("../policies/privacy_policy"));
const CookiesPolicy                   = lazy(() => import("../policies/cookies_policy"));
const RefundPolicy                    = lazy(() => import("../policies/refund_policy"));

// App-shell pages — lazy chunks; Suspense boundary lives inside AppShell so the
// sidebar and navbar stay mounted and never flicker during route transitions.
const DashBoard                       = lazy(() => import("../dash_board"));
const MyServices                      = lazy(() => import("../my_services/my_services"));
const ServiceDetails                  = lazy(() => import("../service_details/service_details"));
const LegalDocumentation              = lazy(() => import("../legal_documentation"));
const OrderAndPayments                = lazy(() => import("../order_and_payments"));
const Notifications                   = lazy(() => import("../notifications"));
const SettingsLayout                  = lazy(() => import("../settings"));
const ComplianceCalendar              = lazy(() => import("../calendar/compliance_calendar"));
const MyCalendar                      = lazy(() => import("../calendar/my_calendar/index"));
const ClientDetails                   = lazy(() => import("../client_details"));
const ServiceManagement               = lazy(() => import("../service_management/service_management"));
const ServiceManagementCreateCategory = lazy(() => import("../service_management_create_category"));
const CustomerServiceManagement       = lazy(() => import("../customer_management"));
const EmployeeManagement              = lazy(() => import("../employee_management"));
const EmployeeDetails                 = lazy(() => import("../employee_management/employee_details"));
const EmployeeTransfer                = lazy(() => import("../employee_management/employee_transfer"));
const ReferralManagement              = lazy(() => import("../referral_management"));
const ClientReferralManagement        = lazy(() => import("../referral_management/client_referral_management"));
const Vault                           = lazy(() => import("../vault"));
const DocumentManagement              = lazy(() => import("../document_management"));
const NotFound                        = lazy(() => import("../not_found"));

// Full-screen fallback for public lazy routes (signin, landing, onboarding).
// Protected-route lazy chunks are caught by AppShell's inner <Suspense> so
// the sidebar and navbar never disappear during authenticated navigations.
const PageFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div
      className="w-8 h-8 rounded-full border-4 border-primary-blue animate-spin"
      style={{ borderTopColor: "transparent" }}
    />
  </div>
);

function Root() {
  const { permissions } = usePermissions();

  const p = permissions;
  const sm = p?.service_management;
  const com = p?.client_or_order_management;
  const hr = p?.hr_management;

  const canAgentViewServiceDetails =
    sm?.enabled &&
    sm?.features?.service_dashboard_list?.subFeatures?.view_service_list_and_details?.enabled;

  const canAgentViewDocuments = p?.document_management?.enabled;

  const canAgentViewBilling = p?.orders_payments?.enabled;

  const canAgentViewComplianceCalendar = p?.compliance_calendar?.enabled;

  const canAgentViewClientDetails =
    com?.enabled &&
    com?.features?.employee_assignee?.enabled &&
    com?.features?.employee_assignee?.subFeatures?.view_the_list_of_all_clients?.enabled;

  const canAgentViewOrderDetails =
    com?.enabled &&
    com?.features?.employee_assignee?.enabled &&
    com?.features?.employee_assignee?.subFeatures?.view_the_list_of_all_orders?.enabled;

  const canAgentManageServices =
    sm?.enabled &&
    sm?.features?.service_dashboard_list?.subFeatures?.view_service_list_and_details?.enabled;

  const canAgentManageEmployees = hr?.enabled;

  const canAgentTransferEmployee =
    hr?.enabled &&
    hr?.features?.for_employee_transfer?.enabled &&
    hr?.features?.for_employee_transfer?.subFeatures?.take_action_transfer_employee_button_access_for_employee?.enabled;

  const canAgentViewEmployeeDetails =
    hr?.enabled &&
    hr?.features?.employee_dashboard?.enabled &&
    hr?.features?.employee_dashboard?.subFeatures?.view_employee?.enabled;

  const canAgentViewReferralManagement = p?.referral_management?.enabled;

  const canAgentManageServiceCategory =
    sm?.enabled &&
    sm?.features?.service_dashboard_list?.enabled &&
    sm?.features?.service_dashboard_list?.subFeatures?.manage_category_service?.enabled;

  const canAgentViewVault =
    p?.password_vault?.enabled &&
    p?.password_vault?.features?.can_view_vault_dashboard?.enabled &&
    p?.password_vault?.features?.can_view_vault_dashboard?.subFeatures?.view_clients_list?.enabled;

  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      {/* ── Public / unauthenticated (no sidebar) ── */}
      <Route path="/" element={<RedirectIfAuthenticated><LandingPage /></RedirectIfAuthenticated>} />
      <Route path="/register" element={<RedirectIfAuthenticated><Register /></RedirectIfAuthenticated>} />
      <Route path="/signin" element={<RedirectIfAuthenticated><ClientSignIn /></RedirectIfAuthenticated>} />
      <Route path="/refer/:referalCode" element={<RedirectIfAuthenticated><ClientSignIn /></RedirectIfAuthenticated>} />
      <Route path="/agent/signin" element={<RedirectIfAuthenticated><SignIn /></RedirectIfAuthenticated>} />
      <Route path="/resetPassword" element={<RedirectIfAuthenticated><ResetPassword /></RedirectIfAuthenticated>} />
      <Route path="/terms_and_conditions" element={<RedirectIfAuthenticated><TermsAndConditions /></RedirectIfAuthenticated>} />
      <Route path="/privacy_policy" element={<RedirectIfAuthenticated><PrivacyPolicy /></RedirectIfAuthenticated>} />
      <Route path="/cookies_policy" element={<RedirectIfAuthenticated><CookiesPolicy /></RedirectIfAuthenticated>} />
      <Route path="/refund_policy" element={<RedirectIfAuthenticated><RefundPolicy /></RedirectIfAuthenticated>} />
      <Route path="/onboarding" element={<EmployeeOnBoarding />} />
      <Route path="/client-onboarding" element={<ClientOnBoarding />} />

      {/* ── App shell: sidebar + navbar stay mounted; only page content suspends ── */}
      <Route element={<AppShell />}>
        <Route path="/dashboard" element={<ProtectedRoute><DashBoard /></ProtectedRoute>} />
        <Route path="/all-services" element={<ProtectedRoute allowedUserTypes={["CLIENT"]}><ServiceManagement /></ProtectedRoute>} />
        <Route path="/my-services" element={<ProtectedRoute allowedUserTypes={["CLIENT"]}><MyServices /></ProtectedRoute>} />
        <Route
          path="/service-details"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentViewServiceDetails ? "AGENT" : "", "CLIENT"].filter(Boolean)}>
              <ServiceDetails />
            </ProtectedRoute>
          }
        />
        <Route path="/legal-documentation" element={<ProtectedRoute><LegalDocumentation /></ProtectedRoute>} />
        <Route
          path="/document-management"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentViewDocuments ? "AGENT" : "", "CLIENT"].filter(Boolean)}>
              <DocumentManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentViewBilling ? "AGENT" : "", "CLIENT"].filter(Boolean)}>
              <OrderAndPayments />
            </ProtectedRoute>
          }
        />
        <Route path="/notifications" element={<ProtectedRoute allowedUserTypes={["AGENT", "CLIENT"]}><Notifications /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsLayout /></ProtectedRoute>} />
        <Route
          path="/compliance-calendar"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentViewComplianceCalendar ? "AGENT" : "", "CLIENT"].filter(Boolean)}>
              <ComplianceCalendar />
            </ProtectedRoute>
          }
        />
        <Route path="/my-calendar" element={<ProtectedRoute allowedUserTypes={["AGENT", "CLIENT"]}><MyCalendar /></ProtectedRoute>} />
        <Route
          path="/client-details"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentViewClientDetails ? "AGENT" : ""].filter(Boolean)}>
              <ClientDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/order-details"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentViewOrderDetails ? "AGENT" : ""].filter(Boolean)}>
              <ClientDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-management"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentManageServices ? "AGENT" : ""].filter(Boolean)}>
              <ServiceManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee-management"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentManageEmployees ? "AGENT" : ""].filter(Boolean)}>
              <EmployeeManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee-transfer"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentTransferEmployee ? "AGENT" : ""].filter(Boolean)}>
              <EmployeeTransfer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee-details"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentViewEmployeeDetails ? "AGENT" : ""].filter(Boolean)}>
              <EmployeeDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/referral-management"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentViewReferralManagement ? "AGENT" : ""].filter(Boolean)}>
              <ReferralManagement />
            </ProtectedRoute>
          }
        />
        <Route path="/client-referral-management" element={<ProtectedRoute><ClientReferralManagement /></ProtectedRoute>} />
        <Route
          path="/service-create-category"
          element={
            <ProtectedRoute allowedUserTypes={[canAgentManageServiceCategory ? "AGENT" : ""].filter(Boolean)}>
              <ServiceManagementCreateCategory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vault"
          element={
            <ProtectedRoute allowedUserTypes={["CLIENT", canAgentViewVault ? "AGENT" : ""].filter(Boolean)}>
              <Vault />
            </ProtectedRoute>
          }
        />
        <Route path="/customer-service-management" element={<ProtectedRoute><CustomerServiceManagement /></ProtectedRoute>} />
      </Route>

      {/* ── 404 catch-all (no shell) ── */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

export default Root;
